import { useState } from 'react';
import { notification } from 'antd';
import { t } from '../i18n/i18n';
import {
  getCompaniesPaginated,
  getCompanySearches,
  getSearchInvitations,
} from '../API/services/companyService';
import {
  getRestartScreeningData,
  postRestartPrefilterTest,
  postRestartTestgroup,
} from '../API/services/screeningService';
import processQueryString from '../utils/processQueryString';
import { TestGroupTypes } from '../constants/enums';

const useResetTestgroup = () => {
  const [step, setStep] = useState(1);
  const [companies, setCompanies] = useState([]);
  const [companySearches, setCompanySearches] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState();
  const [selectedSearch, setSelectedSearch] = useState();
  const [screeningData, setScreeningData] = useState();
  const [selectedTestgroups, setSelectedTestgroups] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [selectedInvitations, setSelectedInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [selectedInstances, setSelectedInstances] = useState([]);
  const [selectedPrefilterInstances, setSelectedPrefilterInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restartPrefilters, setRestartPrefilter] = useState(false);

  const handlePostData = async () => {
    if (!restartPrefilters) {
      const dataToPost = {
        testGroupIds: selectedTestgroups,
        instanceIds: selectedInstances,
      };
      await postRestartTestgroup(dataToPost).then(() => {
        notification.open({
          message: t('POST_RESET_TESTGROUP_NOTIFICATION'),
          type: 'info',
        });
        setStep(5);
      });
    } else {
      const dataToPost = {
        testGroupIds: selectedTestgroups,
        instanceIds: selectedPrefilterInstances,
      };
      await postRestartPrefilterTest(dataToPost).then(() => {
        notification.open({
          message: t('POST_RESET_TESTGROUP_NOTIFICATION'),
          type: 'info',
        });
        setStep(5);
      });
    }
  };
  const fetchCompanies = async (search = '') => {
    setLoading(true);
    await getCompaniesPaginated(search).then((res) => {
      setCompanies(res);
      setLoading(false);
    });
  };

  const fetchCompanySearches = async ({ companyId, search }) => {
    setLoading(true);
    await getCompanySearches({ companyId, search }).then((res) => {
      setCompanySearches(res);
      setLoading(false);
    });
  };

  const fetchSearchInvitations = async ({ search }) => {
    setLoading(true);
    const recruitSessionStage = selectedSearch.stages[0];
    await getSearchInvitations({ search, stage: recruitSessionStage?.id }).then((res) => {
      setInvitations(res);
      setLoading(false);
    });
  };

  const fetchSearchInvitationsNextPage = async () => {
    setLoadingInvitations(true);
    await getSearchInvitations(processQueryString(invitations?.pagination?.nextPage)).then(
      (res) => {
        setInvitations({
          collection: [...invitations.collection, ...res.collection],
          pagination: res.pagination,
        });
        setLoadingInvitations(false);
      }
    );
  };

  const fetchSearchScreening = async () => {
    setLoading(true);
    const recruitSessionStage = selectedSearch.stages[0];
    await getRestartScreeningData({
      levelId: recruitSessionStage.levelId,
      companyId: recruitSessionStage.companyId,
    }).then((res) => {
      setScreeningData(res);
      setLoading(false);
    });
  };

  const handleSelectTestgroup = (id) => {
    // Comportamiento de selección individual (igual que en RestartTestgroupModal)
    if (selectedTestgroups.includes(id)) {
      setSelectedTestgroups(selectedTestgroups.filter(testgroupId => testgroupId !== id));
    } else {
      setSelectedTestgroups([...selectedTestgroups, id]);
    }
  };

  const handleSelectInstance = (invitation) => {
    if (selectedInstances.includes(invitation.screeningInstanceId)) {
      setSelectedInstances(
        selectedInstances.filter((item) => item !== invitation.screeningInstanceId)
      );
      setSelectedPrefilterInstances(
        selectedPrefilterInstances.filter((item) => item !== invitation?.prefilterInstanceId)
      );
      setSelectedInvitations(
        selectedInvitations.filter(
          (item) => item.screeningInstanceId !== invitation.screeningInstanceId
        )
      );
    } else {
      setSelectedInstances([...selectedInstances, invitation.screeningInstanceId]);
      setSelectedPrefilterInstances([
        ...selectedPrefilterInstances,
        invitation?.prefilterInstanceId,
      ]);
      setSelectedInvitations([...selectedInvitations, invitation]);
    }
  };

  const handleSelectCompany = (id) => {
    setSelectedCompany(id);
  };

  const handleSelectSearch = (id) => {
    setSelectedSearch(id);
  };

  const handleNextStep = () => {
    if (step < 4) {
      setStep(step + 1);
    } else {
      handlePostData();
    }
  };

  const handlePreviousStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const clearSelectedInvitations = () => {
    setSelectedInvitations([]);
    setSelectedInstances([]);
    setSelectedPrefilterInstances([]);
  };

  const clearSelectedTestgroups = () => {
    setSelectedTestgroups([]);
  };

  return {
    step,
    companies,
    companySearches,
    loading,
    selectedCompany,
    selectedSearch,
    screeningData,
    testgroups: screeningData?.testGroups ?? [],
    selectedTestgroups,
    selectedInstances,
    selectedPrefilterInstances,
    invitations,
    selectedInvitations,
    loadingInvitations,
    restartPrefilters,
    setRestartPrefilter,
    fetchSearchInvitationsNextPage,
    fetchSearchScreening,
    fetchCompanies,
    fetchCompanySearches,
    fetchSearchInvitations,
    handleSelectTestgroup,
    handleSelectCompany,
    handleSelectSearch,
    handleSelectInstance,
    handlePreviousStep,
    handleNextStep,
    clearSelectedInvitations,
    clearSelectedTestgroups,
  };
};

export default useResetTestgroup;
